/**
 * Venue drill-down opened from the Cities page venue table.
 *
 * Evan (2026-08-01): "if I click on a venue on the city page I should be able to see much more."
 *
 * Structure deliberately mirrors the existing modal detail views (`UserPostsModal`,
 * `UserFriendsModal`, `VenueEditModal`): fixed overlay → bordered card → header row with a Close
 * button → scrolling body → loading / error / empty states in that order.
 *
 * Reads only. Every column below was verified against PRODUCTION `public.venues`
 * (2026-08-01) — see the two-tier select fallback for why that still isn't assumed.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { useEscToClose } from '../lib/useEscToClose';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonRows } from '../ui/Skeleton';

export type VenueDetailSeed = {
  id: string;
  name: string;
  /** Live counts the Cities page already computed from `logs` — shown next to the denormalized columns. */
  ratings: number;
  uniqueRaters: number;
};

type VenueDetailRow = {
  id: string;
  venue_name_user_input: string | null;
  venue_type: string | null;
  venue_type_confidence: number | null;
  venue_type_contributor_count: number | null;
  venue_type_locked: boolean | null;
  venue_shado_score: number | null;
  venue_shado_unique_users: number | null;
  venue_shado_rating_count: number | null;
  venue_shado_last_updated: string | null;
  community_price: string | null;
  price_contributor_count: number | null;
  community_peak_time_weekday: number | null;
  community_peak_time_weekend: number | null;
  peak_time_contributor_count: number | null;
  latitude: number | null;
  longitude: number | null;
  coords_approximate: boolean | null;
  locality: string | null;
  region: string | null;
  country_code: string | null;
  fsq_address: string | null;
  fsq_place_id: string | null;
  fsq_category: string | null;
  fsq_popularity: number | null;
  fsq_rating: number | null;
  fsq_total_ratings: number | null;
  apple_place_id: string | null;
  google_place_id: string | null;
  source: string | null;
  platform_source: string | null;
  approved_venue_photo_url: string | null;
  google_photo_r2_url: string | null;
  google_fetch_disabled: boolean | null;
  is_visible: boolean | null;
  is_hidden: boolean | null;
  created_at: string | null;
  updated_at: string | null;
};

/**
 * Columns an OPERATOR acts on: scoring, community aggregates, placement, identity, visibility.
 * Deliberately not `select('*')` — `venues` has 60+ columns and most are ingest bookkeeping
 * (`osm_*`, `*_lookup_attempted_at`, `_city_id_processed`, `geom`) that would only add noise.
 */
const VENUE_DETAIL_SELECT_FULL = [
  'id',
  'venue_name_user_input',
  'venue_type',
  'venue_type_confidence',
  'venue_type_contributor_count',
  'venue_type_locked',
  'venue_shado_score',
  'venue_shado_unique_users',
  'venue_shado_rating_count',
  'venue_shado_last_updated',
  'community_price',
  'price_contributor_count',
  'community_peak_time_weekday',
  'community_peak_time_weekend',
  'peak_time_contributor_count',
  'latitude',
  'longitude',
  'coords_approximate',
  'locality',
  'region',
  'country_code',
  'fsq_address',
  'fsq_place_id',
  'fsq_category',
  'fsq_popularity',
  'fsq_rating',
  'fsq_total_ratings',
  'apple_place_id',
  'google_place_id',
  'source',
  'platform_source',
  'approved_venue_photo_url',
  'google_photo_r2_url',
  'google_fetch_disabled',
  'is_visible',
  'is_hidden',
  'created_at',
  'updated_at',
].join(', ');

/** Always-present subset — PostgREST rejects the WHOLE select if one column is missing. */
const VENUE_DETAIL_SELECT_CORE =
  'id, venue_name_user_input, venue_type, venue_shado_score, venue_shado_unique_users, venue_shado_rating_count, latitude, longitude, fsq_popularity, created_at';

type VenuePhotoRow = {
  id: string;
  public_url: string | null;
  created_at: string;
  display_order: number | null;
  source: string | null;
};

type VenueLogRow = {
  id: string;
  user_id: string | null;
  elo_score: number | null;
  venue_score: number | null;
  notes: string | null;
  mood_tag: string | null;
  created_at: string;
  visit_date: string | null;
  is_public: boolean | null;
  is_event: boolean | null;
  retired_at: string | null;
  deleted_at: string | null;
};

const RECENT_LOG_LIMIT = 25;

function missingColumnError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('does not exist') || m.includes('42703') || m.includes('schema cache') || m.includes('column');
}

/**
 * Peak time is stored as a float hour that can exceed 24 (a 1:30am peak is `25.5`), and `.5`
 * means half past. Formatter copied from the app so admin and device read the SAME string —
 * `screens/venue-discovery.tsx:379 formatPeakTime`.
 */
function formatPeakTime(val: number): string {
  const normalized = val >= 24 ? val - 24 : val;
  const hours = Math.floor(normalized);
  const minutes = normalized % 1 === 0.5 ? '30' : '00';
  const period = val < 24 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes} ${period}`;
}

function fmtInt(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? Math.round(v).toLocaleString() : '—';
}

function fmtFloat(v: number | null | undefined, digits = 2): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '—';
}

function fmtText(v: string | null | undefined): string {
  const s = typeof v === 'string' ? v.trim() : '';
  return s || '—';
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function fmtBool(v: boolean | null | undefined): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">{title}</h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 rounded-lg border border-border bg-rowAlt px-4 py-3 sm:grid-cols-2">
        {children}
      </dl>
    </section>
  );
}

function Row({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 py-1 last:border-b-0">
      <dt className="shrink-0 text-[11px] text-mutedDim">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right text-[12px] text-white tabular-nums ${
          mono ? 'font-mono text-[11px]' : ''
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function Badge({ tone, children }: { tone: 'muted' | 'amber' | 'red' | 'violet'; children: ReactNode }) {
  const cls = {
    muted: 'border-border bg-white/[0.04] text-muted',
    amber: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
    red: 'border-red-500/40 bg-red-500/15 text-red-200',
    violet: 'border-violet-500/40 bg-violet-500/15 text-violet-200',
  }[tone];
  return (
    <span
      className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {children}
    </span>
  );
}

export function CityVenueDetailModal({
  venue,
  cityName,
  onClose,
}: {
  venue: VenueDetailSeed | null;
  cityName: string | null;
  onClose: () => void;
}) {
  const [row, setRow] = useState<VenueDetailRow | null>(null);
  const [photos, setPhotos] = useState<VenuePhotoRow[]>([]);
  const [logs, setLogs] = useState<VenueLogRow[]>([]);
  const [usernameById, setUsernameById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEscToClose(Boolean(venue), onClose);

  const load = useCallback(async (venueId: string) => {
    setLoading(true);
    setError(null);
    try {
      /**
       * Three narrow, indexed reads — never a scan. `venues` is by primary key;
       * `venue_photos` rides `venue_photos_venue_approved_idx (venue_id) WHERE approved`;
       * `logs` is bounded to the newest 25 rows for this venue. All three stay O(1) in the
       * size of the 3.3M-row `venues` table.
       */
      const [venueRes, photoRes, logRes] = await Promise.all([
        supabase.from('venues').select(VENUE_DETAIL_SELECT_FULL).eq('id', venueId).maybeSingle(),
        supabase
          .from('venue_photos')
          .select('id, public_url, created_at, display_order, source')
          .eq('venue_id', venueId)
          .eq('approved', true)
          .order('display_order', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(24),
        supabase
          .from('logs')
          .select(
            'id, user_id, elo_score, venue_score, notes, mood_tag, created_at, visit_date, is_public, is_event, retired_at, deleted_at',
          )
          .eq('venue_id', venueId)
          .order('created_at', { ascending: false })
          .limit(RECENT_LOG_LIMIT),
      ]);

      let venueRow = venueRes.data as VenueDetailRow | null;
      if (venueRes.error) {
        // An additive column absent in this environment must not blank the whole modal —
        // retry with the always-present core columns (same guard style as `loadCities`).
        if (!missingColumnError(venueRes.error.message)) throw venueRes.error;
        const retry = await supabase
          .from('venues')
          .select(VENUE_DETAIL_SELECT_CORE)
          .eq('id', venueId)
          .maybeSingle();
        if (retry.error) throw retry.error;
        venueRow = retry.data as VenueDetailRow | null;
      }
      if (photoRes.error) throw photoRes.error;
      if (logRes.error) throw logRes.error;

      const logRows = (logRes.data ?? []) as VenueLogRow[];
      setRow(venueRow);
      setPhotos((photoRes.data ?? []) as VenuePhotoRow[]);
      setLogs(logRows);

      // Resolve usernames in ONE extra request rather than a PostgREST embed — `logs.user_id`
      // points at `auth.users`, so there is no FK for PostgREST to traverse to `profiles`.
      const ids = Array.from(
        new Set(logRows.map((l) => l.user_id).filter((id): id is string => Boolean(id))),
      );
      if (ids.length > 0) {
        const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
        const map: Record<string, string> = {};
        for (const p of (profs ?? []) as { id: string; username: string | null }[]) {
          if (p.username) map[p.id] = p.username;
        }
        setUsernameById(map);
      } else {
        setUsernameById({});
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load venue');
      setRow(null);
      setPhotos([]);
      setLogs([]);
      setUsernameById({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (venue?.id) void load(venue.id);
  }, [venue?.id, load]);

  /**
   * The denormalized counters are maintained by trigger; the Cities page counts the same thing
   * live off `logs`. When they disagree the recompute has drifted — worth showing, not hiding.
   */
  const countsDrift = useMemo(() => {
    if (!venue || !row) return false;
    const denormRaters = row.venue_shado_unique_users;
    const denormRatings = row.venue_shado_rating_count;
    if (typeof denormRaters === 'number' && denormRaters !== venue.uniqueRaters) return true;
    if (typeof denormRatings === 'number' && denormRatings !== venue.ratings) return true;
    return false;
  }, [venue, row]);

  if (!venue) return null;

  const coords =
    typeof row?.latitude === 'number' && typeof row?.longitude === 'number'
      ? `${row.latitude.toFixed(5)}, ${row.longitude.toFixed(5)}`
      : '—';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`Venue detail — ${venue.name}`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-xl border border-border bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="admin-label-primary truncate text-base">{venue.name}</h2>
              {row?.venue_type ? <Badge tone="violet">{row.venue_type}</Badge> : null}
              {row?.is_hidden ? <Badge tone="red">Hidden</Badge> : null}
              {row?.is_visible === false ? <Badge tone="amber">Not visible</Badge> : null}
            </div>
            <div className="mt-1 text-xs text-mutedDim">
              {cityName?.trim() ? `${cityName} · ` : ''}
              {venue.ratings} rating{venue.ratings === 1 ? '' : 's'} · {venue.uniqueRaters} unique rater
              {venue.uniqueRaters === 1 ? '' : 's'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            Close
          </button>
        </div>

        <div className="max-h-[75vh] space-y-5 overflow-y-auto px-5 py-4">
          {loading ? (
            <SkeletonRows rows={8} />
          ) : error ? (
            <ErrorBanner>{error}</ErrorBanner>
          ) : !row ? (
            <EmptyState
              title="Venue not found"
              hint="The row may have been merged or deleted since this city list was loaded."
            />
          ) : (
            <>
              {photos.length > 0 ? (
                <section>
                  <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                    Approved photos ({photos.length})
                  </h3>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {photos.map((p) => {
                      const isPrimary =
                        Boolean(p.public_url) && p.public_url === row.approved_venue_photo_url;
                      return (
                        <div key={p.id} className="relative shrink-0">
                          <div className="h-24 w-24 overflow-hidden rounded-lg border border-border bg-black">
                            {p.public_url ? (
                              <img
                                src={p.public_url}
                                alt=""
                                referrerPolicy="no-referrer"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-[10px] text-mutedDim">
                                no url
                              </div>
                            )}
                          </div>
                          {isPrimary ? (
                            <span className="absolute left-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                              Primary
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <Section title="Scoring">
                <Row label="Shado score" value={fmtFloat(row.venue_shado_score, 1)} />
                <Row
                  label="Unique users (column)"
                  value={
                    <span className={countsDrift ? 'text-amber-300' : undefined}>
                      {fmtInt(row.venue_shado_unique_users)}
                    </span>
                  }
                />
                <Row
                  label="Rating count (column)"
                  value={
                    <span className={countsDrift ? 'text-amber-300' : undefined}>
                      {fmtInt(row.venue_shado_rating_count)}
                    </span>
                  }
                />
                <Row label="Live logs / raters" value={`${venue.ratings} / ${venue.uniqueRaters}`} />
                <Row label="Score last updated" value={fmtDate(row.venue_shado_last_updated)} />
                <Row label="Type confidence" value={fmtFloat(row.venue_type_confidence)} />
              </Section>

              {countsDrift ? (
                <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
                  The denormalized counters disagree with a live count of `logs` for this venue —
                  `recompute_venue_shado_score` has drifted for this row.
                </p>
              ) : null}

              <Section title="Community aggregates">
                <Row label="Price" value={fmtText(row.community_price)} />
                <Row label="Price contributors" value={fmtInt(row.price_contributor_count)} />
                <Row
                  label="Peak — weekday"
                  value={
                    typeof row.community_peak_time_weekday === 'number'
                      ? formatPeakTime(row.community_peak_time_weekday)
                      : '—'
                  }
                />
                <Row
                  label="Peak — weekend"
                  value={
                    typeof row.community_peak_time_weekend === 'number'
                      ? formatPeakTime(row.community_peak_time_weekend)
                      : '—'
                  }
                />
                <Row label="Peak contributors" value={fmtInt(row.peak_time_contributor_count)} />
                <Row label="Type contributors" value={fmtInt(row.venue_type_contributor_count)} />
                <Row label="Type locked" value={fmtBool(row.venue_type_locked)} />
              </Section>

              <Section title="Placement">
                <Row label="Coordinates" value={coords} mono />
                <Row label="Coords approximate" value={fmtBool(row.coords_approximate)} />
                <Row label="Locality" value={fmtText(row.locality)} />
                <Row label="Region" value={fmtText(row.region)} />
                <Row label="Country" value={fmtText(row.country_code)} />
                <Row label="Address (FSQ)" value={fmtText(row.fsq_address)} />
              </Section>

              <Section title="Foursquare">
                <Row label="Popularity" value={fmtFloat(row.fsq_popularity, 3)} />
                <Row label="FSQ rating" value={fmtFloat(row.fsq_rating, 1)} />
                <Row label="FSQ total ratings" value={fmtInt(row.fsq_total_ratings)} />
                <Row label="Category" value={fmtText(row.fsq_category)} />
                <Row label="FSQ place id" value={fmtText(row.fsq_place_id)} mono />
              </Section>

              <Section title="Identity & photos">
                <Row label="Source" value={fmtText(row.source)} />
                <Row label="Platform source" value={fmtText(row.platform_source)} />
                <Row label="Apple place id" value={fmtText(row.apple_place_id)} mono />
                <Row label="Google place id" value={fmtText(row.google_place_id)} mono />
                <Row label="Has approved hero" value={fmtBool(Boolean(row.approved_venue_photo_url))} />
                <Row label="Has Google hero" value={fmtBool(Boolean(row.google_photo_r2_url))} />
                <Row label="Google fetch disabled" value={fmtBool(row.google_fetch_disabled)} />
                <Row label="Created" value={fmtDate(row.created_at)} />
                <Row label="Updated" value={fmtDate(row.updated_at)} />
              </Section>

              <section>
                <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                  Recent logs (newest {RECENT_LOG_LIMIT})
                </h3>
                {logs.length === 0 ? (
                  <EmptyState compact title="No logs yet" hint="Nobody has rated this venue." />
                ) : (
                  <ul className="space-y-1.5">
                    {logs.map((l) => (
                      <li
                        key={l.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-rowAlt px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-[12px] text-white">
                            @{l.user_id ? usernameById[l.user_id] ?? l.user_id.slice(0, 8) : 'unknown'}
                          </div>
                          {l.notes?.trim() ? (
                            <div className="truncate text-[11px] text-mutedDim">{l.notes}</div>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {l.retired_at ? <Badge tone="amber">Retired</Badge> : null}
                          {l.deleted_at ? <Badge tone="red">Deleted</Badge> : null}
                          {l.is_event ? <Badge tone="muted">Event</Badge> : null}
                          {l.is_public === false ? <Badge tone="muted">Private</Badge> : null}
                          <span className="text-[11px] tabular-nums text-muted">
                            {typeof l.elo_score === 'number' ? Math.round(l.elo_score) : '—'}
                          </span>
                          <span className="w-20 text-right text-[11px] text-mutedDim">
                            {timeAgo(l.created_at)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
